import React from 'react';
import {
  IconShoppingBag,
  IconMail,
  IconBuildingStore,
  IconArrowRight,
  IconSettings,
  IconDeviceDesktopAnalytics,
} from '@tabler/icons-react';
import { useStore } from '../store/Store';
import { useMantineColorScheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';

interface AppAcquisitionCTAProps {
  className?: string;
  style?: React.CSSProperties;
}

export const AppAcquisitionCTA: React.FC<AppAcquisitionCTAProps> = ({ className, style }) => {
  const { t } = useTranslation();
  const userProfile = useStore((state) => state.userProfile);
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Convert hex color to RGB for gradient use
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 102, g: 126, b: 234 }; // fallback
  };

  const primaryColor = userProfile.qrCodeColor || '#667eea';
  const { r, g, b } = hexToRgb(primaryColor);
  const containerStyle: React.CSSProperties = {
    backgroundColor: 'var(--mantine-color-body)',
    borderRadius: '12px',
    padding: '48px 32px',
    textAlign: 'center',
    color: 'var(--mantine-color-text)',
    position: 'relative',
    overflow: 'hidden',
    maxWidth: '600px',
    margin: '0 auto',
    boxShadow: isDark ? '0 10px 25px rgba(0, 0, 0, 0.3)' : '0 10px 25px rgba(0, 0, 0, 0.15)',
    border: `2px solid ${primaryColor}`,
    ...style,
  };

  const iconContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${primaryColor} 0%, rgba(${r}, ${g}, ${b}, 0.8) 100%)`,
    margin: '0 auto 24px',
    position: 'relative',
    zIndex: 2,
    color: '#ffffff',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: '700',
    margin: '0 0 16px 0',
    position: 'relative',
    zIndex: 2,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '400',
    margin: '0 0 8px 0',
    opacity: 0.9,
    position: 'relative',
    zIndex: 2,
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    lineHeight: '1.6',
    margin: '0 0 32px 0',
    opacity: 0.85,
    position: 'relative',
    zIndex: 2,
    textAlign: 'justify',
  };

  const ctaButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 32px',
    backgroundColor: primaryColor,
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '1.1rem',
    fontWeight: '600',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 2,
  };

  const handleEmailClick = () => {
    const subject = encodeURIComponent(t('cta.emailSubject'));
    const body = encodeURIComponent(t('cta.emailBody'));
    window.location.href = `mailto:service@robotop.de?subject=${subject}&body=${body}`;
  };

  const featuresStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    margin: '32px 0',
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 2,
  };

  const featureStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.95rem',
    opacity: 0.9,
  };

  return (
    <div className={className} style={containerStyle}>
      <div style={iconContainerStyle}>
        <IconDeviceDesktopAnalytics size={40} />
      </div>

      <h1 style={titleStyle}>{t('cta.title')}</h1>

      <p style={subtitleStyle}>{t('cta.subtitle')}</p>

      <p style={descriptionStyle}>{t('cta.description')}</p>

      <div style={featuresStyle}>
        <div style={featureStyle}>
          <IconBuildingStore size={16} />
          <span>{t('cta.feature1')}</span>
        </div>
        <div style={featureStyle}>
          <IconSettings size={16} />
          <span>{t('cta.feature2')}</span>
        </div>
        <div style={featureStyle}>
          <IconShoppingBag size={16} />
          <span>{t('cta.feature3')}</span>
        </div>
      </div>

      <button
        onClick={handleEmailClick}
        style={ctaButtonStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.8)`;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 25px rgba(${r}, ${g}, ${b}, 0.3)`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = primaryColor;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <IconMail size={20} />
        <span>{t('cta.button')}</span>
        <IconArrowRight size={20} />
      </button>
    </div>
  );
};
